/**
 * Woodcut client (I8b): E at a tree starts a 3-beat chop, a ring shrinks to each beat and E on the beat hits.
 * Sends chopStart / chopHit and reads the chopBeat events. Placeholder visuals: a DOM ring and Thai text.
 */
import type { PlayerCommand, World, WorldEvent } from '@nine/shared';

/** Beat period (systems/chopBeat.ts CHOP_BEAT.period). */
const CHOP_BEAT = { period: 0.8 };

export class Woodcut {
  private el: HTMLDivElement;
  private auto: HTMLButtonElement;
  private autoOn = false; // อัตโนมัติ: repeat loop (systems/autoGather.ts)
  private ring: HTMLDivElement;
  private txt: HTMLDivElement;
  private active = false;
  private t = 0;
  private hide = 0;
  private logs = 0;
  private resin = 0;

  constructor(
    private readonly world: World,
    private readonly send: (c: PlayerCommand) => void,
  ) {
    this.el = document.createElement('div');
    this.el.style.cssText =
      'position:fixed;left:50%;bottom:30%;transform:translateX(-50%);width:120px;height:120px;z-index:60;' +
      'pointer-events:none;display:none;text-align:center;color:#ffe9a8;font:16px sans-serif';
    this.ring = document.createElement('div');
    this.ring.style.cssText = 'position:absolute;left:50%;top:50%;border:3px solid #ffd35a;border-radius:50%';
    const target = document.createElement('div');
    target.style.cssText =
      'position:absolute;left:30px;top:30px;width:54px;height:54px;border:2px solid #fff8;border-radius:50%';
    this.txt = document.createElement('div');
    this.txt.style.cssText = 'position:absolute;top:124px;left:-60px;width:240px;background:#000a;padding:2px 6px';
    this.el.append(target, this.ring, this.txt);
    document.body.appendChild(this.el);
    this.auto = document.createElement('button');
    this.auto.style.cssText =
      'position:fixed;left:50%;bottom:16%;transform:translateX(-50%);padding:4px 12px;background:#000c;color:#ffe9a8;' +
      'border:2px solid #c9a04a;font:15px sans-serif;z-index:61;display:none;cursor:pointer';
    this.auto.onclick = () => this.send({ t: 'autoGather', on: !this.autoOn });
    document.body.appendChild(this.auto);
  }

  key(): boolean {
    if (this.active) {
      this.send({ t: 'chopHit' });
      return true;
    }
    const n = this.world.trades.nodeNear(this.world.player);
    if (n?.kind.terrain !== 'tree') return false;
    this.send({ t: 'chopStart' });
    return true;
  }

  handle(ev: WorldEvent): void {
    if ((ev.type as string) === 'autoGather') this.autoOn = (ev as { on: boolean }).on;
    if (ev.type === 'chopBeatStart') {
      this.active = true;
      this.t = 0;
      this.logs = this.resin = 0;
      this.hide = 0;
      this.el.style.display = '';
      this.txt.textContent = 'ตัดไม้ กดตามจังหวะ';
    } else if (ev.type === 'chopBeat') {
      this.logs += ev.log;
      this.resin += ev.resin;
      this.txt.textContent = ev.onBeat ? 'โดนจังหวะ!' : 'พลาดจังหวะ';
      if (ev.done) {
        this.active = false;
        this.txt.textContent = `ได้ไม้สน ×${this.logs}` + (this.resin ? ` ยางสน ×${this.resin}` : '');
        this.hide = 2.2;
      }
    }
  }

  sync(dt: number): void {
    const near = this.world.trades.nodeNear(this.world.player)?.kind.terrain === 'tree';
    this.auto.style.display = near || this.autoOn ? '' : 'none';
    this.auto.textContent = this.autoOn ? 'อัตโนมัติ: เปิด' : 'อัตโนมัติ';
    if (this.active) {
      this.t += dt;
      const f = (this.t % CHOP_BEAT.period) / CHOP_BEAT.period; // 0 just after a beat .. 1 at the next
      const d = 54 + (1 - f) * 56;
      const s = this.ring.style;
      s.width = s.height = `${d}px`;
      s.marginLeft = s.marginTop = `${-d / 2 - 3}px`;
      s.display = '';
    } else this.ring.style.display = 'none';
    if (this.hide > 0 && (this.hide -= dt) <= 0) this.el.style.display = 'none';
  }
}
